import React, {useEffect, useState} from 'react';
import Modal from "react-bootstrap/Modal";
import {
    FaBook,
    FaBookOpen,
    FaClock,
    FaCoins,
    FaExclamationTriangle, FaFilter, FaFlag,
    FaHourglassHalf,
    FaStar,
    FaUser
} from "react-icons/fa";
import Row from "react-bootstrap/Row";
import {Col} from "react-bootstrap";

const InfoAssUnderBookS3 = ({ show, handleClose, assignment }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const date = new Date(assignment.publish_rev_date * 60 * 1000);

    // минуты -> миллисекунды (безопасно)
    const minutesToMs = (m) => (Number.isFinite(m) ? m * 60 * 1000 : NaN);

    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!assignment?.publish_rev_date) return;

        const deadlineMs = (assignment.publish_rev_date + 20160) * 60 * 1000; // unix-время в минутах → ms

        const updateTimeLeft = () => {
            const diff = deadlineMs - Date.now();

            if (diff <= 0) {
                setTimeLeft({
                    expired: true,
                    days: 0,
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                });
                return;
            }

            const totalSeconds = Math.floor(diff / 1000);
            const days = Math.floor(totalSeconds / (24 * 60 * 60));
            const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            setTimeLeft({
                expired: false,
                days,
                hours,
                minutes,
                seconds,
            });
        };

        updateTimeLeft();
        const timerId = setInterval(updateTimeLeft, 1000);

        return () => clearInterval(timerId);
    }, [assignment?.publish_rev_date]);

    // 1) Длинная дата + короткое время. По умолчанию en-GB (24h).
// Для AM/PM можно вызвать с { locale: 'en-US' }.
    const formatDateLongFromMinutes = (
        minutes,
        {
            locale = 'en-GB',
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
        } = {}
    ) => {
        const ms = minutesToMs(minutes);
        if (!Number.isFinite(ms)) return 'N/A';
        const d = new Date(ms);
        try {
            return new Intl.DateTimeFormat(locale, {
                dateStyle: 'long',  // e.g. "12 June 2025"
                timeStyle: 'short', // e.g. "22:15"
                timeZone,
            }).format(d);
        } catch {
            return d.toLocaleString(locale, { timeZone });
        }
    };

// 2) Длительность между двумя моментами (в минутах) => "Xd, YH, ZM"
    const formatDurationBetweenMinutes = (
        startMinutes,
        endMinutes = Math.floor(Date.now() / (60 * 1000))
    ) => {
        const s = minutesToMs(startMinutes);
        const e = minutesToMs(endMinutes);
        if (!Number.isFinite(s) || !Number.isFinite(e)) return 'N/A';

        const diffMs = Math.max(0, e - s);
        const totalMin = Math.floor(diffMs / (1000 * 60));
        const d = Math.floor(totalMin / (60 * 24));
        const h = Math.floor((totalMin % (60 * 24)) / 60);
        const m = totalMin % 60;
        return `${d}D, ${h}H, ${m}M`;
    };

// 3) Синоним: «сколько прошло с момента»
    const formatElapsedFromMinutes = (startMinutes) =>
        formatDurationBetweenMinutes(startMinutes);

    // ✅ Полные названия месяцев
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    // ✅ Получаем дату начала чтения
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    const formattedHours = hours < 10 ? `0${hours}` : hours;

    // ✅ Вычисляем, сколько времени прошло с момента начала чтения
    const now = new Date();
    const elapsedTime = now - date;
    const elapsedMinutes = Math.floor(elapsedTime / (1000 * 60));
    const elapsedHours = Math.floor(elapsedMinutes / 60);
    const elapsedDays = Math.floor(elapsedHours / 24);

    const remainingHours = elapsedHours % 24;
    const remainingMinutes = elapsedMinutes % 60;
    return (
        <Modal show={show} onHide={() => { setError(""); handleClose(); }} top>
            <div className="modal-content">
                <div className="modal-header small" style={{height:40}}>
                    <h6 className="modal-title fs-6">Assignment: #{assignment?.bookId}-{assignment?.id}</h6>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => {
                            setError("");
                            handleClose();
                        }}
                    ></button>
                </div>

                <div className="modal-body small">
                    <p
                        className=" mb-2 fs-6 mt-0 text-muted"
                    >
                        <FaHourglassHalf className="me-2 fa-spin" />

                        <strong className="  m-0"> Posted, awaiting approval...</strong>
                    </p>

                    {/* Posted */}
                    {assignment?.start_rev_date && (
                        <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                            <FaBook className="me-2" />
                            Posted: <strong>{formatDateLongFromMinutes(assignment?.start_rev_date)}</strong>
                        </p>
                    )}

                    {/* Start Reading */}
                    {assignment?.start_read_date && (
                        <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                            <FaBookOpen className="me-2" />
                            Start: <strong>{formatDateLongFromMinutes(assignment?.start_read_date)}</strong>
                        </p>
                    )}

                    {/* Reported */}
                    {assignment?.publish_rev_date && (
                        <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                            <FaFlag className="me-2" />
                            Reported: <strong>{formatDateLongFromMinutes(assignment?.publish_rev_date)}</strong>
                        </p>
                    )}

                    {assignment?.publish_rev_date && (
                        <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                            <FaClock className="me-2  " />
                            Waiting:{" "}
                            <strong>
                                {formatElapsedFromMinutes(assignment?.publish_rev_date)}
                            </strong>
                        </p>
                    )}

                    <p className="mb-1 mt-0 text-muted">
                        <FaBook className="me-2 mb-0 mt-0 text-muted" />
                        Assignment for:
                        <strong className="  m-1">
                            {(() => {
                                switch (assignment?.booksTypeId) {
                                    case 1: return "Book in Kindle,";
                                    case 2: return "Book in Paperback,";
                                    case 3: return "Book in Hardcover,";
                                    case 4: return "Audiobook,";
                                    case 5: return "Boost Existing Review,";
                                    case 6: return "Report Existing Review,";
                                    case 7: return "Physical Product,";
                                    case 8: return "Physical Product,";
                                    case 9: return "Physical Product,";
                                    case 10: return "Physical Product,";
                                    default:
                                        return "N/A";
                                }
                            })()}
                        </strong>
                    </p>

                    <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                        <FaBook className="me-2" />
                        Type:{" "}
                        <strong className="  m-0">
                            {(() => {
                                switch (assignment.assTypeId) {
                                    case 1: return "Universal Assignment";
                                    case 2: return "Kindle Unlimited";
                                    case 3: return "Kindle Verified Purchase";
                                    case 4: return "Kindle Verified Purchase + Img";
                                    case 5: return "Kindle Verified Purchase Search&Buy";
                                    case 6: return "Kindle Verified Search&Buy + Img";
                                    case 7: return "Universal Assignment for Audio";
                                    case 8: return "Audible Promo Code Assignment";
                                    case 9: return "Verified Kindle Unlimited";
                                    case 10: return "Paperback BuyOut";
                                    case 11: return "Paperback Search&BuyOut";
                                    case 12: return "Paperback Verified Purchase + Img";
                                    case 13: return "Paperback Verified Purchase + Video";
                                    case 14: return "HC BuyOut";
                                    case 15: return "HC Search&BuyOut";
                                    case 16: return "HC Verified Pur. + Img";
                                    case 17: return "HC Verified + Video";
                                    case 18: return "PB Verified Purchase";
                                    case 19: return "PB Verified Purchase Search&Buy";
                                    case 20: return "HC Verified Purchase";
                                    case 21: return "HC Verified Purchase Search&Buy";
                                    case 22: return "Product BuyOut";
                                    case 23: return "Product Search&BuyOut";
                                    case 24: return "Product Verified Purchase";
                                    case 25: return "Product Verified + Img";
                                    case 26: return "Product Verified + Vid";
                                    case 27: return "Product VP Search&Buy";
                                    case 28: return "Make Helpful Internal";
                                    case 29: return "Write Report Internal";
                                    case 30: return "Make Helpful External";
                                    case 31: return "Write Report External";
                                    case 32: return "Set Stars Rating";
                                    default:
                                        return "Unknown Type"; // ✅ Защита от неверных значений
                                }
                            })()}
                        </strong>
                    </p>

                    <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                        <FaCoins className="me-2" />
                        Reward / Cost: <strong className="m-0">{assignment.assignment_price_publisher || "Unknown"}</strong> Virt /
                        <b className="text-danger">
                            {[10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27].includes(assignment?.assTypeId) ? (
                                assignment?.assignment_price_publisher
                                    ? ` $${assignment.book_cost_compensation}`
                                    : "N/A"
                            ) : (
                                (() => {
                                    let priceLabel;
                                    switch (assignment?.booksPriceId) {
                                        case 1: priceLabel = " Free"; break;
                                        case 2: priceLabel = " $0.99"; break;
                                        case 3: priceLabel = " $1.99"; break;
                                        case 4: priceLabel = " $2.99"; break;
                                        case 5: priceLabel = " $3.99"; break;
                                        case 6: priceLabel = " $4.99"; break;
                                        case 7: priceLabel = " $5.99"; break;
                                        case 8: priceLabel = " $6.99"; break;
                                        case 9: priceLabel = " $7.99"; break;
                                        case 10: priceLabel = " $8.99"; break;
                                        case 11: priceLabel = " $9.99"; break;
                                        default: priceLabel = " Unknown Type";
                                    }

                                    // Добавляем " Promo" при нужных условиях
                                    if (assignment?.booksPriceId === 1 && [3, 4, 5, 6, 32].includes(assignment?.assTypeId)) {
                                        priceLabel += " Promo";
                                    }

                                    return priceLabel;
                                })()
                            )}




                        </b>

                    </p>

                    <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                        <FaUser className="me-2" />
                        Participant: <strong className="  m-0">"{assignment.reviewer_name || "Unknown"}",</strong>
                    </p>
                    <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                        <FaStar className="me-2" />
                        Expected Rating: <b>
                        {assignment.rating
                            ? `${assignment.rating}.0`
                            : 'Not Applicable'}
                    </b>
                    </p>
                    <p className="me-2 mb-1 mt-0 text-muted" style={{ lineHeight: "1.2" }}>
                        <FaFilter className="me-2" />
                        Country:
                        <b>
                            {(() => {
                                switch (assignment.assignment_prefer_country) {
                                    case "https://www.amazon.com":
                                        return " USA";
                                    case "https://www.amazon.ca":
                                        return " Canada";
                                    case "https://www.amazon.com.au":
                                        return " Australia";
                                    case "https://www.amazon.co.uk":
                                        return " United Kingdom";
                                    case "https://www.amazon.de":
                                        return " Deutschland";
                                    case "https://www.amazon.fr":
                                        return " France";
                                    case "https://www.amazon.it":
                                        return " Italia";
                                    case "https://www.amazon.es":
                                        return " España";
                                    case "https://www.amazon.in":
                                        return " India";
                                    case "https://www.amazon.co.jp":
                                        return " Japan";
                                    case "https://www.amazon.com.br":
                                        return " Brasil";
                                    case "https://www.amazon.nl":
                                        return " Netherlands";
                                    case "https://www.amazon.com.mx":
                                        return " Mexico";
                                    case "https://www.amazon.sg":
                                        return " Singapore";
                                    case "https://www.amazon.cn":
                                        return " China";
                                    default:
                                        return " All";
                                }
                            })()}</b>
                        {assignment?.rating_of_reviewer > 20 && (
                            <span className="ms-0 fw-semibold text-success">
                                        {" "}/ Quality: {assignment.rating_of_reviewer}%
                                     </span>
                        )}
                    </p>
                    {assignment.why_not_5_text && (
                        <Row className="d-flex justify-content-center mt-2"> {/* ✅ Центрируем блок */}
                            <p className="text-dark text-muted " style={{ lineHeight: "1.4" }}>
                                <FaUser className="me-1 fa-shake text-success" /> {/* ✅ Анимированная тревожная иконка */}
                                The reader "{assignment.reviewer_name || "Unknown"}" has sent you a message to help improve your book and rated your book with {assignment.rating || 'Unknown'}.0 stars because:
                            </p>
                            <Col md={11} className="shadow border border-secondary p-3 rounded bg-light text-start"> {/* ✅ 80% ширины + тень + скругление */}

                                    <span className="text-danger fw-semibold">{assignment.why_not_5_text || "No"}</span>

                            </Col>
                            <p className="text-dark text-muted mt-3" style={{ lineHeight: "1.4" }}>
                                You may not agree with this rating, but it is important to take the reader's comment into consideration and take appropriate action.
                            </p>
                        </Row>
                    )}
                    {timeLeft && (
                        <div
                            className="p-2 rounded mt-2 mb-2"
                            style={{
                                backgroundColor: "rgba(255, 243, 205, 0.6)", // мягкий жёлтый инфо-фон
                                border: "1px solid #ffeeba",
                                fontSize: "0.9rem",
                                lineHeight: "1.3"
                            }}
                        >
                            <p className="mb-1 fw-semibold text-dark">
                                <FaHourglassHalf className="me-2 text-danger" />
                                Review Visibility Countdown
                            </p>

                            {!timeLeft.expired ? (
                                <>
                                    <p className="mb-2 text-dark">
                                        The participant reported that the review has been submitted.
                                        We are now waiting for Amazon to display it on the book’s or product page.
                                    </p>

                                    <p className="mb-1 text-dark ">
                                        Time left for the review to appear:
                                        <span className="ms-2 text-danger fw-semibold">
                                            {timeLeft.days}d{" "}
                                            {String(timeLeft.hours).padStart(2, "0")}h{" "}
                                            {String(timeLeft.minutes).padStart(2, "0")}m{" "}
                                            {String(timeLeft.seconds).padStart(2, "0")}s
                                        </span>
                                    </p>

                                    <p className="mb-0 mt-2 text-muted small">
                                        If the review does not appear in time, this assignment will automatically return
                                        to the search pool so a new participant can complete it. Or you can cancel this
                                        assignment yourself and receive a full refund.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="mb-1 text-danger fw-semibold">
                                        The time for the review to appear has expired.
                                    </p>
                                    <p className="mb-0 text-muted small">
                                        Since the review did not appear on Amazon, and out of respect for the reviewer,
                                        we’re allowing a short grace period before this assignment is returned
                                        to the search pool for a new participant.
                                    </p>

                                </>
                            )}
                        </div>
                    )}

                </div>
                <div className="position-absolute bottom-0 start-0 mb-2 ms-3 d-flex align-items-center">
                    {[...Array(5)].map((_, index) => (
                        <i
                            key={index}
                            className={`bi bi-star${index < assignment.rating ? '-fill' : ''}`}
                            style={{
                                color: index < assignment.rating ? 'gold' : 'lightgray',
                                fontSize: '18px',
                                marginRight: '6px'
                            }}
                        ></i>
                    ))} <strong className="text-muted fs-5"> {assignment.rating
                    ? `${assignment.rating}.0`
                    : 'Not Applicable'}</strong>
                </div>

                <div
                    className="modal-footer d-flex align-items-center justify-content-end px-2"
                    style={{ height: 46, paddingTop: 0, paddingBottom: 0 }}
                >
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                            setError("");
                            handleClose();
                        }}
                        disabled={loading}
                    >
                        Close
                    </button>
                </div>

            </div>
        </Modal>
    );
};

export default InfoAssUnderBookS3;
